import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import type { DenialCategory } from '@/lib/normalization/denialNormalization';
import { DEFAULT_APPEAL_TEMPLATES, generateAppealContent, type AppealPlaceholders } from '@/lib/normalization/appealTemplates';

type AppealTemplateRow = Database['public']['Tables']['appeal_templates']['Row'];
type AppealRow = Database['public']['Tables']['appeals']['Row'];

export interface AppealTemplate {
  id: string;
  payerId: string | null;
  planId: string | null;
  denialCategory: DenialCategory;
  templateName: string;
  templateContent: string;
  checklistItems: string[];
  version: number;
  isActive: boolean;
}

export interface Appeal {
  id: string;
  authorizationId: string;
  templateId: string | null;
  denialCategory: DenialCategory;
  generatedContent: string;
  editedContent: string | null;
  status: string;
  submittedAt: Date | null;
  createdAt: Date;
}

interface UseAppealTemplatesOptions {
  payerId?: string;
  planId?: string;
  denialCategory?: DenialCategory;
}

export function useAppealTemplates(options: UseAppealTemplatesOptions = {}) {
  const [templates, setTemplates] = useState<AppealTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('appeal_templates')
        .select('*')
        .eq('is_active', true)
        .order('version', { ascending: false });

      if (options.payerId) {
        query = query.or(`payer_id.eq.${options.payerId},payer_id.is.null`);
      }
      
      if (options.planId) {
        query = query.or(`plan_id.eq.${options.planId},plan_id.is.null`);
      }
      
      if (options.denialCategory) {
        query = query.eq('denial_category', options.denialCategory);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      const mappedTemplates: AppealTemplate[] = (data || []).map((row: AppealTemplateRow) => ({
        id: row.id,
        payerId: row.payer_id,
        planId: row.plan_id,
        denialCategory: row.denial_category as DenialCategory,
        templateName: row.template_name,
        templateContent: row.template_content,
        checklistItems: row.checklist_items || [],
        version: row.version,
        isActive: row.is_active ?? true,
      }));

      setTemplates(mappedTemplates);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [options.payerId, options.planId, options.denialCategory]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const getTemplateForCategory = (
    category: DenialCategory,
    payerId?: string,
    planId?: string
  ): AppealTemplate | null => {
    // Priority: specific payer+plan > payer only > plan only > generic
    const matches = templates.filter(t => t.denialCategory === category);
    
    const exactMatch = matches.find(t => t.payerId === payerId && t.planId === planId);
    if (exactMatch) return exactMatch;
    
    const payerMatch = matches.find(t => t.payerId === payerId && !t.planId);
    if (payerMatch) return payerMatch;
    
    const planMatch = matches.find(t => !t.payerId && t.planId === planId);
    if (planMatch) return planMatch;
    
    const genericMatch = matches.find(t => !t.payerId && !t.planId);
    if (genericMatch) return genericMatch;
    
    // Fall back to default template
    const defaultTemplate = DEFAULT_APPEAL_TEMPLATES[category];
    return {
      id: `default-${category}`,
      payerId: null,
      planId: null,
      denialCategory: category,
      templateName: defaultTemplate.template_name,
      templateContent: defaultTemplate.template_content,
      checklistItems: defaultTemplate.checklist_items,
      version: defaultTemplate.version,
      isActive: true,
    };
  };

  const createTemplate = async (template: Omit<AppealTemplate, 'id' | 'version' | 'isActive'>) => {
    const { data: { user } } = await supabase.auth.getUser();
    
    const { data, error: insertError } = await supabase
      .from('appeal_templates')
      .insert({
        payer_id: template.payerId,
        plan_id: template.planId,
        denial_category: template.denialCategory,
        template_name: template.templateName,
        template_content: template.templateContent,
        checklist_items: template.checklistItems,
        created_by: user?.id,
      })
      .select()
      .single();

    if (insertError) throw insertError;
    await fetchTemplates();
    return data;
  };

  const updateTemplate = async (id: string, updates: Partial<AppealTemplate>) => {
    const { error: updateError } = await supabase
      .from('appeal_templates')
      .update({
        template_name: updates.templateName,
        template_content: updates.templateContent,
        checklist_items: updates.checklistItems,
      })
      .eq('id', id);

    if (updateError) throw updateError;
    await fetchTemplates();
  };

  return {
    templates,
    loading,
    error,
    refetch: fetchTemplates,
    getTemplateForCategory,
    createTemplate,
    updateTemplate,
  };
}

// Hook for managing appeals for a specific authorization
export function useAppeals(authorizationId: string | null) {
  const [appeals, setAppeals] = useState<Appeal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchAppeals = useCallback(async () => {
    if (!authorizationId) {
      setAppeals([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('appeals')
        .select('*')
        .eq('authorization_id', authorizationId)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      const mappedAppeals: Appeal[] = (data || []).map((row: AppealRow) => ({
        id: row.id,
        authorizationId: row.authorization_id,
        templateId: row.template_id,
        denialCategory: row.denial_category as DenialCategory,
        generatedContent: row.generated_content,
        editedContent: row.edited_content,
        status: row.status,
        submittedAt: row.submitted_at ? new Date(row.submitted_at) : null,
        createdAt: new Date(row.created_at),
      }));

      setAppeals(mappedAppeals);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [authorizationId]);

  useEffect(() => {
    fetchAppeals();
  }, [fetchAppeals]);

  const createAppeal = async (
    templateId: string | null,
    denialCategory: DenialCategory,
    generatedContent: string
  ) => {
    if (!authorizationId) return;

    const { data: { user } } = await supabase.auth.getUser();

    const { data, error: insertError } = await supabase
      .from('appeals')
      .insert({
        authorization_id: authorizationId,
        template_id: templateId?.startsWith('default-') ? null : templateId,
        denial_category: denialCategory,
        generated_content: generatedContent,
        status: 'draft',
        created_by: user?.id,
      })
      .select()
      .single();

    if (insertError) throw insertError;
    await fetchAppeals();
    return data;
  };

  const updateAppeal = async (appealId: string, editedContent: string) => {
    const { error: updateError } = await supabase
      .from('appeals')
      .update({ edited_content: editedContent })
      .eq('id', appealId);

    if (updateError) throw updateError;
    await fetchAppeals();
  };

  const submitAppeal = async (appealId: string) => {
    const { data: { user } } = await supabase.auth.getUser();

    const { error: updateError } = await supabase
      .from('appeals')
      .update({
        status: 'submitted',
        submitted_at: new Date().toISOString(),
        submitted_by: user?.id,
      })
      .eq('id', appealId);

    if (updateError) throw updateError;
    await fetchAppeals();
  };

  return {
    appeals,
    loading,
    error,
    refetch: fetchAppeals,
    createAppeal,
    updateAppeal,
    submitAppeal,
  };
}
